// @ts-strict-ignore
import moment from 'moment';
import React, { useCallback, useContext, useMemo } from 'react';
import { Options, RRule } from 'rrule';
import { View } from '@shared/components/View';
import { MBCalendar } from '@shared/components/dateAndTime/calendar';
import { DATE_FORMATS } from '@shared/components/dateAndTime/comboDatePicker';
import { MBComboDatePickerField } from '@shared/components/dateAndTime/comboDatePicker/field';
import { MBTextBody2 } from '@shared/components/typography';
import { ColorThemeContext, RECURRENCE_CARD_COUNT } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { fromRRuleDate, toRRuleDate } from '../utils';

interface Props {
  rruleString: string;
  onChange(rrule: RRule): void;
}

export function MBRecurrenceSelectEndDate({ rruleString, onChange }: Props) {
  const colors = useContext(ColorThemeContext);
  const rrule = useMemo(() => RRule.fromString(rruleString), [rruleString]);

  const minDate = useMemo(
    () => moment(rrule.options.dtstart).startOf('day').add(1, 'day').toISOString(),
    [rrule],
  );
  const maxDate = useMemo(() => {
    const ruleWithoutEnd = new RRule({ ...rrule.options, until: undefined, count: undefined });
    const dates = ruleWithoutEnd.all((_date, num) => num < RECURRENCE_CARD_COUNT);
    return dates[dates.length - 1]?.toISOString();
  }, [rrule]);

  const handleChangeUntil = useCallback(
    (dateString: string) => {
      const selectedDate = moment(toRRuleDate(new Date(dateString)));
      const updatedOptions: Options = {
        ...rrule.options,
        count: undefined,
        until: moment.min(moment.max(selectedDate, moment(minDate)), moment(maxDate)).toDate(),
      };

      onChange(new RRule(updatedOptions));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rrule, onChange],
  );
  const until = useMemo(
    () => (!!rrule.options.until ? fromRRuleDate(rrule.options.until) : undefined),
    [rrule.options.until],
  );
  return (
    <View padding={2}>
      <MBTextBody2 color={colors.foregroundActive}>
        {i18n('card.blocks.recurrenceInput.ends.specificDate.description', {
          count: RECURRENCE_CARD_COUNT,
        })}
      </MBTextBody2>
      <View paddingTop={2} />
      <MBComboDatePickerField
        icon="calendar"
        label={i18n('card.blocks.recurrenceInput.ends.specificDate.label')}
        value={!!until ? moment(until).format('l') : ''}
        formats={DATE_FORMATS}
        allowCustomInput={true}
        onChange={handleChangeUntil}
      />
      <MBCalendar
        onSelectDate={handleChangeUntil}
        selectedDates={useMemo(() => (!!until ? [until.toISOString()] : undefined), [until])}
        minDate={minDate}
        maxDate={maxDate}
      />
    </View>
  );
}
